/**
 * RowAxisFilterRenderer
 *
 * Renders a PlanViewRowDimension's `filterJson` as the embedded condition editor (PlanView-RowAxis-
 * Composition Phase 2) instead of a raw JSON text field. The author builds AND/OR conditions over the
 * row value's fields (code/name, plus backing-specific attributes); the result is stored as a
 * `ConditionDto` JSON string in `filterJson` and evaluated **server-side** by the row-value resolver.
 *
 * The `EntityBackedRowConfigRenderer` gate (rank 25) still owns visibility — it delegates `filterJson`
 * to this renderer (the next-ranked match) for every axis that can carry a filter: entity-backed,
 * ACCOUNT, and free. The contextSchema (which fields are filterable) is served per dimension by
 * Finance, which asks the backing entity's owning module for an entity-backed dimension and reads
 * `DimensionValue`'s own metadata for a free one — Finance never reads another module's schema to
 * author the filter.
 */

import { useMemo } from 'react';
import { rankWith, ControlProps, isControl } from '@jsonforms/core';
import { withJsonFormsControlProps, useJsonForms } from '@jsonforms/react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { CACHE_TIME } from '@/config/queryDefaults';
import { useDimensionDefinitions } from '@/hooks/finance/useDimensionDefinition';
import { buildRowAxisContextSchema, type RowAxisFilterFieldDto } from '@/finance/rowAxisFilterSchema';
import { ConditionJsonControl } from './ConditionJsonControl';

function scopeProperty(uischema: any): string | undefined {
  const scope: string | undefined = uischema?.scope;
  return scope ? scope.split('/').pop() : undefined;
}

const RowAxisFilterRendererComponent = (props: ControlProps) => {
  const { data, handleChange, path, errors, visible } = props;
  const ctx = useJsonForms();
  const { data: definitions } = useDimensionDefinitions();

  const selectedId = (ctx.core?.data as any)?.dimensionDefinitionId as string | undefined;
  // An ACCOUNT axis (rowSourceKind = ACCOUNT) has no dimension: its filterable fields are the GL
  // account's own attributes, served by the account variant of the filter-fields endpoint.
  const isAccount = (ctx.core?.data as any)?.rowSourceKind === 'ACCOUNT';
  // The entity the filtered rows ARE — the context schema's name. An entity-backed dimension's rows are
  // its backing entity's records; a free dimension's are its own DimensionValue rows.
  const filteredEntity = useMemo(() => {
    const definition = definitions?.find((d) => d.id === selectedId);
    if (!definition) return undefined;
    return definition.valueSource === 'ENTITY_BACKED' ? definition.backingEntity : 'DimensionValue';
  }, [definitions, selectedId]);

  // The filterable fields come from the owning module (via Finance), so the editor offers the entity's
  // real fields — and only fields the server-side resolver can actually evaluate.
  const { data: fields } = useQuery({
    queryKey: ['rowAxisFilterFields', isAccount ? 'ACCOUNT' : selectedId],
    queryFn: async () =>
      (await apiClient.get<RowAxisFilterFieldDto[]>(
        isAccount
          ? '/api/finance/planline/account-row-axis-filter-fields'
          : `/api/finance/planline/row-axis-filter-fields/${selectedId}`)).data,
    enabled: isAccount || !!selectedId,
    staleTime: CACHE_TIME.METADATA,
  });

  // Pass null while loading so the editor shows its own loading state rather than an empty field list.
  const contextSchema = useMemo(
    () => (fields ? buildRowAxisContextSchema(fields, isAccount ? 'Account' : (filteredEntity ?? 'RowValue')) : null),
    [fields, filteredEntity, isAccount],
  );

  // filterJson is stored as a JSON string (schema type ["string","null"]); an empty/incomplete
  // condition clears the filter (all values). Both halves live in the shared control.
  return (
    <ConditionJsonControl
      value={data}
      onChange={(json) => handleChange(path, json)}
      contextSchema={contextSchema}
      error={errors}
      visible={visible}
    />
  );
};

export const RowAxisFilterRenderer = withJsonFormsControlProps(RowAxisFilterRendererComponent);

/**
 * Tester — matches only the PlanViewRowDimension `filterJson` control (same rootSchema guard as the
 * entity-backed gate). Rank 20: above the text renderers, below the rank-25 gate, so the gate still
 * controls entity-backed visibility and then delegates `filterJson` here.
 */
export const rowAxisFilterTester = rankWith(20, (uischema, _schema, context) => {
  if (!isControl(uischema)) return false;
  if (scopeProperty(uischema) !== 'filterJson') return false;
  const rootProps = (context?.rootSchema as any)?.properties;
  return rootProps?.rowEnumeration?.enumType === 'RowEnumeration'
    && !!rootProps?.dimensionDefinitionId;
});

export const rowAxisFilterEntry = {
  tester: rowAxisFilterTester,
  renderer: RowAxisFilterRenderer,
};
